// Copyright (c) Bentley Systems
import { Button, MenuItem, MenuItemSkeleton, type SelectOption } from "@itwin/itwinui-react";
import { SvgCheckmark } from "@itwin/itwinui-icons-react";
import { forwardRef, type HTMLAttributes, type JSX } from "react";

/**
 * Wrapper that consumes the `focused` boolean prop injected by itwinui's ComboBox
 * via React.cloneElement, preventing it from being forwarded to the DOM as a
 * non-boolean HTML attribute and triggering a React warning. The ref injected by
 * ComboBox is forwarded to the underlying `div`.
 */
const LoadMoreWrapper = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & { focused?: boolean }>(
  ({ focused: _focused, ...props }, ref) => <div ref={ref} {...props} />,
);
LoadMoreWrapper.displayName = "LoadMoreWrapper";

/** Sentinel value used to identify the "Load more" option inside a ComboBox. */
export const LOAD_MORE_VALUE = "__load_more__" as const;

/**
 * Appends a "Load more..." option to the end of a ComboBox options array
 * when there are more pages to fetch.
 *
 * Works with any value type — the returned array uses a union so the
 * sentinel can coexist with domain-specific values (string, ITwin, etc.).
 */
export function appendLoadMoreOption<T>(
  options: SelectOption<T>[],
  hasMore: boolean,
): SelectOption<T | typeof LOAD_MORE_VALUE>[] {
  return hasMore
    ? [...options, { value: LOAD_MORE_VALUE, label: "Load more..." }]
    : options;
}

type PaginatedItemRendererOptions = {
  /** Callback to fetch the next page of results. */
  onLoadMore: () => void;
  /** Whether a page is currently being fetched (shows "Loading..." text). */
  isLoading: boolean;
};

/**
 * Creates a ComboBox `itemRenderer` that:
 * - Renders a "Load more" button for the sentinel option
 * - Renders a `MenuItem` with a `SvgCheckmark` endIcon for selected items
 *
 * Works with any ComboBox value type (`string`, `ITwin`, etc.).
 *
 * @example
 * ```tsx
 * <ComboBox
 *   options={appendLoadMoreOption(selectOptions, hasMore)}
 *   itemRenderer={paginatedItemRenderer({
 *     onLoadMore: fetchNextPage,
 *     isLoading,
 *   })}
 * />
 * ```
 */
export function paginatedItemRenderer<T>({
  onLoadMore,
  isLoading,
}: PaginatedItemRendererOptions) {
  return (
    { value, label }: SelectOption<T | typeof LOAD_MORE_VALUE>,
    { isSelected, id, isFocused }: { isSelected: boolean; id: string; isFocused: boolean },
  ) => {
    if (value === LOAD_MORE_VALUE) {
      return (
        <LoadMoreWrapper
          key={id}
          id={id}
          role="option"
          aria-selected={false}
          style={{ display: "flex", justifyContent: "center", padding: "4px" }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Button
            styleType="borderless"
            size="small"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onLoadMore();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onLoadMore();
            }}
            style={{ fontStyle: "italic", width: "100%" }}
          >
            {isLoading ? "Loading..." : "Load more..."}
          </Button>
        </LoadMoreWrapper>
      );
    }

    return (
      <MenuItem
        key={id}
        id={id}
        isSelected={isSelected}
        focused={isFocused}
        endIcon={isSelected ? <SvgCheckmark /> : undefined}
      >
        {label}
      </MenuItem>
    );
  };
}

/**
 * Builds a ComboBox placeholder string that appends the minimum
 * character hint automatically.
 *
 * @example
 * searchPlaceholder("Search iTwin project", 3)
 * // → "Search iTwin project (min 3 chars)"
 *
 * searchPlaceholder("Search iTwin project", 3, true)
 * // → "Loading..."
 */
export function searchPlaceholder(
  label: string,
  minChars: number,
  isLoading?: boolean,
): string {
  if (isLoading) { return "Loading..."; }
  return `${label} (min ${minChars} chars)`;
}

/**
 * Returns an appropriate empty-state message for a search ComboBox
 * based on the current input length vs. the required minimum.
 *
 * @example
 * searchEmptyMessage("", 3)          // → "Type to search"
 * searchEmptyMessage("ab", 3)        // → "Type at least 3 characters to search"
 * searchEmptyMessage("abc", 3)       // → "No options found"
 */
export function searchEmptyMessage(
  searchString: string,
  minChars: number,
  noResultsMessage = "No options found",
): string {
  const trimLen = searchString.trim().length;
  if (trimLen === 0) { return "Type to search"; }
  if (trimLen < minChars) { return `Type at least ${minChars} characters to search`; }
  return noResultsMessage;
}

/**
 * Returns skeleton loading placeholders for use as ComboBox `emptyStateMessage`
 * while results are being fetched.
 */
export function loadingSkeleton(count = 6): JSX.Element {
  return (
    <>
      {new Array(count).fill(null).map((_, index) => (
        <MenuItemSkeleton
          key={index}
          contentWidth={`${Math.min(Math.max(Math.random() * 100, 25), 60)}%`}
        />
      ))}
    </>
  );
}
